import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "../lib/api";

/**
 * Vanessa-authored landing page at /v/:slug.
 * Client-side fetch + render for fast human nav; crawlers hit the SSR backend
 * route at /api/v/{slug} which returns the same content as fully-rendered HTML.
 */
export default function LandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [anchors, setAnchors] = useState({});
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let alive = true;
    setStatus("loading");
    (async () => {
      try {
        const [pRes, aRes] = await Promise.all([
          client.get(`/landing/pages/${slug}`),
          client.get(`/landing/anchor-text`),
        ]);
        if (!alive) return;
        setPage(pRes.data.page || pRes.data);
        setAnchors(aRes.data || {});
        setStatus("ok");
      } catch (e) {
        if (!alive) return;
        setStatus(e?.response?.status === 404 ? "notfound" : "error");
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // Dynamic head tags for SPA navigation (SSR route also sets these for crawlers)
  useEffect(() => {
    if (!page) return;
    document.title = `${page.title} · TheSaiyanVictoria Nexus`;
    const ensure = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [k, v] = selector.replace(/['"]/g, "").replace("meta[", "").replace("]", "").split("=");
        el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    if (page.meta_description) ensure('meta[name="description"]', "content", page.meta_description);
    if (page.canonical_url) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
      link.setAttribute("href", page.canonical_url);
    }
  }, [page]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="landing-loading">
        <div className="font-mono text-xs text-[#FFB800] uppercase tracking-[0.3em]">Loading transmission…</div>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="landing-404">
        <div className="text-center">
          <span className="text-[10px] text-[#FFB800] uppercase tracking-[0.3em]">404</span>
          <h1 className="text-3xl font-black text-white mt-2 mb-4">Landing page not found</h1>
          <Link to="/" className="text-[#FF6B00] uppercase tracking-widest text-xs font-bold">↗ Enter the Nexus</Link>
        </div>
      </div>
    );
  }

  if (status === "error" || !page) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="landing-error">
        <div className="text-center">
          <h1 className="text-xl text-white">Couldn&apos;t load this page.</h1>
          <Link to="/" className="text-[#FF6B00] uppercase tracking-widest text-xs font-bold mt-4 inline-block">↗ Back to Nexus</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="landing-page">
      <main className="max-w-3xl mx-auto px-6 pt-12 pb-24">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] text-[#FFB800] uppercase tracking-[0.3em] font-bold">TheSaiyanVictoria · Nexus</span>
          {page.persona && (
            <span data-testid="landing-persona" className="text-[10px] text-[#76FFE1] uppercase tracking-[0.2em]">{page.persona}</span>
          )}
        </div>

        <h1 data-testid="landing-title" className="text-4xl sm:text-5xl font-black text-white mt-3 leading-tight tracking-tight">
          {page.title}
        </h1>

        {page.thumbnail_url && (
          <div className="mt-6 aspect-video rounded-xl overflow-hidden bg-zinc-900 border border-white/5">
            <img src={page.thumbnail_url} alt={page.title} className="w-full h-full object-cover" />
          </div>
        )}

        {page.body_html && (
          <article
            data-testid="landing-body"
            className="prose prose-invert prose-zinc mt-8 max-w-none text-zinc-300 leading-relaxed [&_p]:text-base [&_p]:mb-4 [&_a]:text-[#FFB800] [&_a]:no-underline [&_a:hover]:underline [&_h2]:text-white [&_h2]:font-black [&_h2]:mt-8 [&_h3]:text-white"
            dangerouslySetInnerHTML={{ __html: page.body_html }}
          />
        )}

        {page.platform_links && Object.keys(page.platform_links).length > 0 && (
          <div data-testid="landing-platforms" className="mt-10 flex flex-wrap gap-2.5">
            {Object.entries(page.platform_links)
              .filter(([, url]) => !!url)
              .map(([platform, url]) => (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  data-testid={`landing-platform-${platform}`}
                  className="inline-block px-4 py-2.5 rounded-full bg-zinc-900 border border-[#FF6B00]/40 text-[#FFB800] font-bold text-xs uppercase tracking-[0.05em] hover:bg-gradient-to-r hover:from-[#FF6B00] hover:to-[#FFB800] hover:text-black hover:border-transparent transition-colors"
                >
                  {anchors[platform] || platform}
                </a>
              ))}
          </div>
        )}

        {page.seo_tags && page.seo_tags.length > 0 && (
          <div data-testid="landing-tags" className="mt-6 flex flex-wrap gap-1.5">
            {page.seo_tags.map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full bg-[#FFB800]/10 text-[#FFB800] text-[11px] tracking-wide">
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-14 pt-8 border-t border-white/5 text-center">
          <Link to="/" className="text-[#FF6B00] text-xs font-black uppercase tracking-[0.1em]">
            ↗ Enter the Nexus
          </Link>
        </div>
      </main>
    </div>
  );
}
